import React, { useEffect, useState } from 'react';
import type { AiJob } from '../../../lib/supabase.types';
import type { ApprovalSummary, BuildSummary, OperatorSummary, OutlineHeading, PageSection, SummaryChanges, SummaryRevision } from '../../../lib/aiJobSummary';
import { aiJobKind } from '../../../lib/aiJobLabels';
import { useTrelloLink } from '../../../lib/trelloCardUrl';
import { formatForDateInput } from '../../../utils/dateUtils';
import { Badge, Button, Icon } from '../../ui';
import AiMarkdown from '../AiMarkdown';
import ClientNoteEditor from './ClientNoteEditor';
import { SubmitNoteBar } from './SubmissionNote';
import type { SubmissionNoteControl } from './useSubmissionNote';
import PageSections from './PageSections';
import PageKeyword from './PageKeyword';
import PageBuyerCard from './PageBuyerCard';
import SeoReviewCard from './SeoReviewCard';
import BuyerTestCard from './BuyerTestCard';
import ContentOutline from './ContentOutline';
import NeedToKnow from './NeedToKnow';
import RequestTimeline from './RequestTimeline';
import SecurityAuditReport, { SecurityVerificationCard } from './SecurityAuditReport';
import SectionHeading, { LinkButton, OpBullets, OpCard } from './SectionHeading';

/**
 * The Overview tab. With a summary: the structured operator page. Without
 * one: exactly the markdown sections the drawer showed, so every job without
 * a summary still opens fine.
 */
export default function OperatorOverview({
  job,
  summary,
  readOnly = false,
  changes = null,
  onMakeTask,
  onAnswerDecision,
  onSaveClientNote,
  onSaveOutline,
  onSaveSections,
  onSaveKeyword,
  submissionNote,
}: {
  job: AiJob;
  summary: OperatorSummary | null;
  readOnly?: boolean;
  /** What changed since the previous version of the current stage. */
  changes?: SummaryChanges | null;
  /** Files a parked item as its own Cockpit task. */
  onMakeTask?: (text: string, dueDate: string) => Promise<void>;
  /** Records a decision answer on the job. */
  onAnswerDecision?: (decisionId: string, answer: string | null) => Promise<void>;
  /** Saves an edited paste-ready client note. */
  onSaveClientNote?: (markdown: string) => Promise<void>;
  /** Content jobs: saves the human's adjusted outline, or null for the agent's. */
  onSaveOutline?: (headings: OutlineHeading[] | null, note: string | null) => Promise<void>;
  /** Page Build jobs: saves the edited sections, or null for the agent's. */
  onSaveSections?: (sections: PageSection[] | null) => Promise<void>;
  /** Page Build jobs: saves a changed target keyword, or null for the agent's pick. */
  onSaveKeyword?: (keyword: { primary: string; secondary: string[] } | null) => Promise<void>;
  /** Client feedback jobs: Submit Note under the client note. Absent elsewhere. */
  submissionNote?: SubmissionNoteControl;
}) {
  // Tasks made from the parked list are due today unless the operator picks
  // another date; one control for the section rather than one per row.
  const [taskDue, setTaskDue] = useState(() => formatForDateInput(new Date()));
  if (!summary?.spec) return <MarkdownFallback job={job} />;
  const { spec, build, approval } = summary;
  const changedCount = changes?.requests.size ?? 0;
  // Content jobs: the outline replaces the repo / database pair and the approach
  // list. Older content summaries without an outline keep the general layout.
  const outline = spec.outline_override?.headings?.length ? spec.outline_override.headings : spec.outline;
  const isArticle = job.kind === 'content' && !!outline?.length;
  // The latest round's answer to a revise note: the build's once there is a build, else the spec's.
  const revision = build ? build.revision : spec.revision;

  // Security Audit jobs: the findings are the page. Their decisions are answered on the rows, so "What you need to
  // know" only carries the caveats and any decision that isn't a finding's.
  if (job.task_type === 'security_audit' && spec.findings) {
    const findingDecisions = new Set(spec.findings.map((f) => f.decision_id));
    return (
      <>
        {spec.greeting ? <Greeting text={spec.greeting} /> : null}
        {revision?.done?.length ? <RevisionCard revision={revision} version={job.version} /> : null}
        {approval ? <ApprovalCard approval={approval} cardId={job.card_id} clientId={job.client_id} onSaveClientNote={onSaveClientNote} submissionNote={submissionNote} /> : null}
        {build?.verification ? <SecurityVerificationCard verification={build.verification} /> : null}
        {build ? <BuildCard build={build} /> : null}
        <NeedToKnow items={spec.need_to_know} decisions={spec.decisions.filter((d) => !findingDecisions.has(d.id))} readOnly={readOnly && !onAnswerDecision} onAnswer={onAnswerDecision} />
        <SecurityAuditReport spec={spec} build={build} readOnly={readOnly && !onAnswerDecision} onAnswer={onAnswerDecision} />
      </>
    );
  }

  return (
    <>
      {spec.greeting ? <Greeting text={spec.greeting} /> : null}
      {revision?.done?.length ? <RevisionCard revision={revision} version={job.version} /> : null}
      {approval ? (
        <ApprovalCard approval={approval} cardId={job.card_id} clientId={job.client_id} onSaveClientNote={onSaveClientNote} submissionNote={submissionNote} />
      ) : null}
      {build ? <BuildCard build={build} /> : null}
      {build?.buyer_test ? <BuyerTestCard test={build.buyer_test} /> : null}
      {build?.seo_review ? <SeoReviewCard review={build.seo_review} /> : null}

      <section>
        <SectionHeading
          eyebrow="Requests · in their words, in their order"
          title="What was asked"
          icon="list-checks"
          count={spec.requests.length}
          actions={
            changedCount > 0 && changes?.previous ? (
              <Badge tone="accent" dot>
                {changedCount} changed since v{changes.previous.job_version}
              </Badge>
            ) : changes && !changes.first && changes.previous ? (
              <Badge tone="neutral">same as v{changes.previous.job_version}</Badge>
            ) : null
          }
        />
        <RequestTimeline spec={spec} build={build} approval={approval} changed={changes?.requests} />
      </section>

      {job.task_type === 'page_build' && spec.sections?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {spec.buyer ? <PageBuyerCard buyer={spec.buyer} /> : null}
          <PageKeyword keyword={spec.keyword} override={spec.keyword_override} readOnly={!!readOnly && !onSaveKeyword} onSave={onSaveKeyword} />
          <PageSections sections={spec.sections} override={spec.sections_override} readOnly={!!readOnly && !onSaveSections} onSave={onSaveSections} />
        </div>
      ) : null}

      {isArticle ? (
        <ContentOutline
          outline={spec.outline ?? []}
          override={spec.outline_override}
          readOnly={readOnly}
          onSave={onSaveOutline}
        />
      ) : null}

      <NeedToKnow
        items={spec.need_to_know}
        decisions={spec.decisions}
        readOnly={readOnly}
        onAnswer={onAnswerDecision}
      />

      {isArticle ? null : (
      <section className="ck-op-pair">
        <OpCard tone="raised">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icon name="git-branch" size={14} style={{ color: 'var(--text-faint)' }} />
            <span className="t-eyebrow">Repo</span>
          </div>
          <div className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', wordBreak: 'break-all' }}>
            {spec.repo.name}
          </div>
          {spec.repo.note ? (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-muted)' }}>
              {spec.repo.note}
            </p>
          ) : null}
        </OpCard>
        <OpCard tone="raised">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icon name="database" size={14} style={{ color: 'var(--text-faint)' }} />
            <span className="t-eyebrow">Database</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <DbRef label="prod" value={spec.db.prod} />
            <DbRef label="preview" value={spec.db.preview} />
          </div>
          {spec.db.note ? (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-muted)' }}>
              {spec.db.note}
            </p>
          ) : null}
        </OpCard>
      </section>
      )}

      {!isArticle && spec.approach.length > 0 ? (
        <section>
          <SectionHeading eyebrow="Approach" title="How it gets done" icon="lightbulb" />
          <OpCard>
            <OpBullets items={spec.approach} />
          </OpCard>
        </section>
      ) : null}

      {spec.parked.length > 0 ? (
        <section>
          <SectionHeading
            eyebrow="Parked"
            title="Deliberately not done"
            icon="pause-circle"
            count={spec.parked.length}
            actions={
              onMakeTask && !readOnly ? (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }} title="Due date for tasks made from this list">
                  <span className="t-eyebrow">New tasks due</span>
                  <input
                    type="date"
                    className="ck-textarea"
                    style={{ height: '28px', minHeight: 0, padding: '0 8px', fontSize: 'var(--text-sm)', resize: 'none', width: 'auto' }}
                    value={taskDue}
                    onChange={(e) => setTaskDue(e.target.value)}
                  />
                </label>
              ) : null
            }
          />
          <OpCard tone="raised" padding="6px 8px">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
              {spec.parked.map((item, i) => (
                <ParkedItem
                  key={i}
                  text={item}
                  first={i === 0}
                  readOnly={readOnly || !onMakeTask}
                  due={taskDue}
                  onMakeTask={onMakeTask}
                />
              ))}
            </ul>
          </OpCard>
        </section>
      ) : null}
    </>
  );
}

function ParkedItem({
  text,
  first,
  readOnly,
  due,
  onMakeTask,
}: {
  text: string;
  first: boolean;
  readOnly: boolean;
  due: string;
  onMakeTask?: (text: string, dueDate: string) => Promise<void>;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const file = async () => {
    if (!onMakeTask || state === 'busy' || state === 'done') return;
    setState('busy');
    try {
      await onMakeTask(text, due);
      setState('done');
    } catch {
      setState('error');
    }
  };
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 8px',
        borderTop: first ? 'none' : '1px solid var(--border-hairline)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-muted)' }}>{text}</span>
      <span className="ck-badge-flip" style={{ flexShrink: 0 }}>
        <span key={state} className="ck-badge-flip-inner">
          <Button
            size="sm"
            variant={state === 'done' ? 'secondary' : 'ghost'}
            icon={state === 'done' ? 'check' : state === 'error' ? 'circle-alert' : 'plus'}
            disabled={readOnly || state === 'busy' || state === 'done'}
            title={readOnly ? 'Live jobs only' : `Files this as a Cockpit task on the client, due ${due || 'today'}, assigned to you`}
            onClick={file}
          >
            {state === 'busy' ? 'Filing…' : state === 'done' ? 'Task made' : state === 'error' ? 'Try again' : 'Make a task'}
          </Button>
        </span>
      </span>
    </li>
  );
}

/** The agent's warm opening line to whoever created the job. Human voice first, structure after. */
function Greeting({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '4px 2px 0' }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '30px',
          height: '30px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--accent-soft)',
          color: 'var(--accent-text)',
          flexShrink: 0,
        }}
      >
        <Icon name="smile" size={16} />
      </span>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-lg)',
          lineHeight: 'var(--leading-normal)',
          letterSpacing: 'var(--tracking-tight)',
          color: 'var(--text-body)',
          maxWidth: '72ch',
        }}
      >
        {text}
      </p>
    </div>
  );
}

/** What this round changed, answering the human's revise note. Sits at the top so the answer is the first thing read. */
function RevisionCard({ revision, version }: { revision: SummaryRevision; version: number }) {
  return (
    <OpCard padding="16px 18px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent-soft)',
            color: 'var(--accent-text)',
            flexShrink: 0,
          }}
        >
          <Icon name="rotate-ccw" size={16} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="t-eyebrow" style={{ marginBottom: '3px' }}>
            Revision{version ? ` · v${version}` : ''}
          </div>
          <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
            What this round changed
          </div>
        </div>
      </div>
      {revision.note ? (
        <blockquote
          style={{
            margin: '0 0 12px',
            padding: '8px 12px',
            borderLeft: '3px solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--surface-raised)',
            fontSize: 'var(--text-sm)',
            lineHeight: 'var(--leading-normal)',
            color: 'var(--text-muted)',
            whiteSpace: 'pre-wrap',
          }}
        >
          <span className="t-eyebrow" style={{ display: 'block', marginBottom: '4px' }}>
            You asked
          </span>
          {revision.note}
        </blockquote>
      ) : null}
      <OpBullets items={revision.done} />
    </OpCard>
  );
}

function DbRef({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
      <span className="t-eyebrow" style={{ width: '56px', flexShrink: 0 }}>
        {label}
      </span>
      <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: value ? 'var(--text-body)' : 'var(--text-faint)', wordBreak: 'break-all' }}>
        {value || 'none'}
      </span>
    </div>
  );
}

function BuildCard({ build }: { build: BuildSummary }) {
  return (
    <OpCard padding="16px 18px">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--accent-soft)',
              color: 'var(--accent-text)',
              flexShrink: 0,
            }}
          >
            <Icon name="git-pull-request" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="t-eyebrow" style={{ marginBottom: '3px' }}>
              Build
            </div>
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
              Ready to review
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {build.pr_url ? (
            <LinkButton href={build.pr_url} variant="secondary" size="md" icon="git-pull-request">
              Pull request
            </LinkButton>
          ) : null}
          {build.preview_url ? (
            <LinkButton href={build.preview_url} variant="primary" size="md" icon="eye">
              Open preview
            </LinkButton>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
        <span className="t-eyebrow">DB written</span>
        <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: build.db_written ? 'var(--text-body)' : 'var(--text-faint)' }}>
          {build.db_written || 'nothing'}
        </span>
      </div>

      {build.deviations.length > 0 ? (
        <div
          style={{
            marginTop: '12px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-warn-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Icon name="triangle-alert" size={13} style={{ color: 'var(--status-warn)' }} />
            <span className="t-eyebrow" style={{ color: 'var(--status-warn)' }}>
              Deviated from the spec
            </span>
          </div>
          {build.deviations.map((d, i) => (
            <span key={i} style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-body)' }}>
              {d}
            </span>
          ))}
        </div>
      ) : null}
    </OpCard>
  );
}

function ApprovalCard({
  approval,
  cardId,
  clientId,
  onSaveClientNote,
  submissionNote,
}: {
  approval: ApprovalSummary;
  cardId?: string | null;
  /** No card on the job: the note's link falls back to this client's board. */
  clientId?: string | null;
  onSaveClientNote?: (markdown: string) => Promise<void>;
  submissionNote?: SubmissionNoteControl;
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const trello = useTrelloLink(cardId, clientId);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(approval.client_note);
      setCopied(true);
    } catch (err) {
      console.warn('Clipboard write failed:', err);
    }
  };

  const liveCount = approval.confirmed.filter((c) => c.status === 'live').length;

  return (
    <OpCard padding="16px 18px">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--status-up-soft)',
              color: 'var(--status-up)',
              flexShrink: 0,
            }}
          >
            <Icon name="rocket" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            {/* No live URL = a content approval: the piece is verified, publishing is a separate step. */}
            <div className="t-eyebrow" style={{ marginBottom: '3px' }}>
              {approval.live_url ? 'Live' : 'Approved'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
                {approval.live_url ? 'Shipped' : 'Verified'}
              </span>
              <Badge tone="up" dot>
                {liveCount}/{approval.confirmed.length} live
              </Badge>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {approval.live_url ? (
            <LinkButton href={approval.live_url} variant="primary" size="md" icon="globe">
              Open live site
            </LinkButton>
          ) : null}
          {trello ? (
            <LinkButton
              href={trello.url}
              variant="secondary"
              size="md"
              icon={trello.kind === 'card' ? 'message-square' : 'external-link'}
            >
              {trello.kind === 'card' ? 'Trello card' : 'Trello board'}
            </LinkButton>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
          <span className="t-eyebrow">Client note · pastes into Trello as shown</span>
          {editing ? null : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              {onSaveClientNote ? (
                <Button size="sm" variant="ghost" icon="pencil" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              ) : null}
              <span className="ck-badge-flip">
                <span key={copied ? 'copied' : 'copy'} className="ck-badge-flip-inner">
                  <Button size="sm" variant={copied ? 'secondary' : 'ghost'} icon={copied ? 'check' : 'copy'} onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </span>
              </span>
            </div>
          )}
        </div>
        {editing && onSaveClientNote ? (
          <ClientNoteEditor
            value={approval.client_note}
            onCancel={() => setEditing(false)}
            onSave={async (markdown) => {
              await onSaveClientNote(markdown);
              setEditing(false);
            }}
          />
        ) : (
          <div
            className="ck-client-note"
            style={{
              margin: 0,
              padding: '10px 14px',
              borderLeft: '2px solid var(--status-up)',
              background: 'var(--surface-raised)',
              borderRadius: '0 var(--radius-md) var(--radius-md) 0',
            }}
          >
            <AiMarkdown content={approval.client_note} />
          </div>
        )}
        {!editing && submissionNote ? <SubmitNoteBar control={submissionNote} /> : null}
      </div>

      {approval.follow_ups.length > 0 ? (
        <div style={{ marginTop: '14px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '8px' }}>
            Follow-ups · {approval.follow_ups.length}
          </div>
          <OpBullets items={approval.follow_ups} muted />
        </div>
      ) : null}
    </OpCard>
  );
}

/** Same sections as the drawer today, in the same order. Traces live in the Trace tab. */
function MarkdownFallback({ job }: { job: AiJob }) {
  const kind = aiJobKind(job.status);
  const isUnwind = job.status.startsWith('unwind');
  const showApproval = !!job.approval_body || job.status === 'complete' || job.status === 'approval_in_progress';
  const showReport = kind === 'build' || !!job.report_body;
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--border-hairline)',
          color: 'var(--text-faint)',
          fontSize: 'var(--text-xs)',
        }}
      >
        <Icon name="file-text" size={13} />
        No operator summary on this job yet. Showing the agents' reports as written.
      </div>
      {showApproval ? (
        <OpCard>
          <div className="t-eyebrow" style={{ marginBottom: '10px' }}>
            Approval report
          </div>
          <AiMarkdown content={job.approval_body} />
        </OpCard>
      ) : null}
      {showReport ? (
        <OpCard>
          <div className="t-eyebrow" style={{ marginBottom: '10px' }}>
            {isUnwind ? 'Unwind report' : 'Build report'}
          </div>
          <AiMarkdown content={job.report_body} />
        </OpCard>
      ) : null}
      <OpCard>
        <div className="t-eyebrow" style={{ marginBottom: '10px' }}>
          Spec
        </div>
        <AiMarkdown content={job.spec_body} />
      </OpCard>
    </>
  );
}
